import React, { useEffect, useState, useCallback } from 'react';
import {
  Button, Table, Input, Popconfirm, Space, message, Tag, Alert, Tooltip,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, SaveOutlined, ReloadOutlined,
  EyeOutlined, EyeInvisibleOutlined, InfoCircleOutlined,
} from '@ant-design/icons';
import { systemConfigApi } from '../services/api-services';

interface EnvVar {
  key: string;
  value: string;
}

const EnvVarsConfig: React.FC = () => {
  const [vars, setVars] = useState<EnvVar[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // 记录哪些行的值处于隐藏状态
  const [hiddenRows, setHiddenRows] = useState<Set<number>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await systemConfigApi.getEnvVars();
      setVars(res || []);
      // 默认隐藏所有值
      const indices = new Set<number>((res || []).map((_: any, i: number) => i));
      setHiddenRows(indices);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    // 过滤掉 key 为空的行
    const valid = vars.filter(v => v.key.trim());
    // 检查重复 key
    const keys = valid.map(v => v.key.trim());
    const dup = keys.find((k, i) => keys.indexOf(k) !== i);
    if (dup) {
      message.error(`变量名 "${dup}" 重复，请检查后再保存`);
      return;
    }
    setSaving(true);
    try {
      await systemConfigApi.updateEnvVars(valid);
      setVars(valid);
      message.success('保存成功，重启服务后生效');
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = () => {
    const newVars = [...vars, { key: '', value: '' }];
    setVars(newVars);
  };

  const handleDelete = (index: number) => {
    setVars(vars.filter((_, i) => i !== index));
    setHiddenRows(prev => {
      const next = new Set<number>();
      prev.forEach(idx => {
        if (idx < index) next.add(idx);
        else if (idx > index) next.add(idx - 1);
      });
      return next;
    });
  };

  const handleKeyChange = (index: number, val: string) => {
    const next = [...vars];
    next[index] = { ...next[index], key: val };
    setVars(next);
  };

  const handleValueChange = (index: number, val: string) => {
    const next = [...vars];
    next[index] = { ...next[index], value: val };
    setVars(next);
  };

  const toggleHidden = (index: number) => {
    setHiddenRows(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const columns = [
    {
      title: '变量名（KEY）',
      dataIndex: 'key',
      width: '38%',
      render: (_: string, __: EnvVar, index: number) => (
        <Input
          value={vars[index]?.key}
          placeholder="如 OPENAI_API_KEY"
          onChange={e => handleKeyChange(index, e.target.value)}
          style={{ fontFamily: 'monospace' }}
        />
      ),
    },
    {
      title: '值（VALUE）',
      dataIndex: 'value',
      render: (_: string, __: EnvVar, index: number) => {
        const hidden = hiddenRows.has(index);
        return (
          <Space.Compact style={{ width: '100%' }}>
            <Input
              value={vars[index]?.value}
              type={hidden ? 'password' : 'text'}
              placeholder="环境变量的值"
              onChange={e => handleValueChange(index, e.target.value)}
              style={{ fontFamily: 'monospace' }}
            />
            <Tooltip title={hidden ? '显示' : '隐藏'}>
              <Button
                icon={hidden ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                onClick={() => toggleHidden(index)}
              />
            </Tooltip>
          </Space.Compact>
        );
      },
    },
    {
      title: '',
      width: 52,
      render: (_: any, __: EnvVar, index: number) => (
        <Popconfirm
          title="确认删除该环境变量？"
          onConfirm={() => handleDelete(index)}
          okText="删除"
          cancelText="取消"
        >
          <Button type="text" danger icon={<DeleteOutlined />} size="small" />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div className="apple-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 16 }}>
        <div>
          <h1 className="apple-page-title" style={{ marginBottom: 4 }}>环境变量</h1>
          <p style={{ color: 'var(--apple-text-secondary)', fontSize: 13, margin: 0 }}>
            在此配置的环境变量将在服务启动时自动注入到进程环境中，可被后端代码、插件和智能体调用。
          </p>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>刷新</Button>
          <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>保存</Button>
        </Space>
      </div>

      <Alert
        type="warning"
        showIcon
        icon={<InfoCircleOutlined />}
        message="修改后需重启服务才能生效。值中的敏感信息（Token、密钥等）默认隐藏显示。"
        style={{ marginBottom: 16 }}
      />

      <div style={{ maxWidth: 860 }}>
        <Table
          dataSource={vars}
          columns={columns}
          rowKey={(_, i) => String(i)}
          pagination={false}
          size="middle"
          loading={loading}
          locale={{ emptyText: '暂无环境变量，点击下方按钮添加' }}
          footer={() => (
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              block
            >
              添加环境变量
            </Button>
          )}
        />

        {vars.length > 0 && (
          <div style={{ marginTop: 12, color: 'var(--apple-text-tertiary)', fontSize: 12 }}>
            共 {vars.filter(v => v.key.trim()).length} 个有效变量
            {vars.some(v => !v.key.trim()) && (
              <Tag color="orange" style={{ marginLeft: 8 }}>
                有 {vars.filter(v => !v.key.trim()).length} 行变量名为空，保存时将自动忽略
              </Tag>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EnvVarsConfig;
